import api from "@/api";

export default {
  namespaced: true,
  state: () => ({
    group: null,
    groups: [],
    currentGroup: null,
    isLoading: false,
    error: null,
    students: [],
    pagination: {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1
    },
  }),
  mutations: {
    SET_CLASS(state, classData) {
      state.class = classData;
    },
    SET_GROUPS(state, groups) {
      state.groups = Array.isArray(groups) ? groups : [];
    },
    SET_CURRENT_GROUP(state, group) {
      state.currentGroup = group;
    },
    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_STUDENTS(state, students) {
      state.students = students;
    },
    SET_GROUP(state, group) {
      state.group = group;
    },
    ADD_GROUP(state, newGroup) {
      state.groups.unshift(newGroup);
    },
    UPDATE_GROUP(state, updatedGroup) {
      // Ensure groups is always treated as array
      if (!Array.isArray(state.groups)) {
        state.groups = [];
      }
      
      const index = state.groups.findIndex(g => g.id === updatedGroup.id);
      if (index !== -1) {
        state.groups.splice(index, 1, updatedGroup);
      } else {
        // If group not found, add it to the array
        state.groups.push(updatedGroup);
      }
    },
    DELETE_GROUP(state, groupId) {
      state.groups = state.groups.filter((g) => g.id !== groupId);
    },
    SET_PAGINATION(state, pagination) {
      state.pagination = {
        currentPage: Array.isArray(pagination.current_page) ? pagination.current_page[0] : pagination.current_page,
        perPage: Array.isArray(pagination.per_page) ? pagination.per_page[0] : pagination.per_page,
        total: Array.isArray(pagination.total) ? pagination.total[0] : pagination.total,
        lastPage: Array.isArray(pagination.last_page) ? pagination.last_page[0] : pagination.last_page,
        from: Array.isArray(pagination.from) ? pagination.from[0] : pagination.from,
        to: Array.isArray(pagination.to) ? pagination.to[0] : pagination.to
      }
    },
    SET_CLASS_GROUPS(state, classGroups) {
      state.groups = classGroups;
    },
    RESET_CURRENT_GROUP(state) {
      state.group = null;
    },
    SET_FILTERS(state, filters) {
      state.filters = { ...state.filters, ...filters };
    },
  },
  actions: {
    async fetchGroups({ commit }, classId) {
      commit("SET_LOADING", true);
      try {
        const response = await api.get(`/classes/${classId}/groups`);
        commit("SET_GROUPS", response.data.data);
        return response.data;
      } catch (error) {
        commit("SET_ERROR", error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async fetchGroupStudents({ commit }, { classId, groupId, page = 1, perPage = 10 }) {
      commit('SET_LOADING', true);
      try {
        const response = await api.get(`/classes/${classId}/groups/${groupId}/students`, {
          params: { page, per_page: perPage }
        });
        
        commit('SET_STUDENTS', response.data.data || []);
        commit('SET_GROUP', response.data.meta?.group || null);
        commit('SET_PAGINATION', {
          currentPage: response.data.meta?.current_page || 1,
          perPage: response.data.meta?.per_page || perPage,
          total: response.data.meta?.total || 0,
          lastPage: response.data.meta?.last_page || 1
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching group students:', error);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },


  async assignGroupToClass({ commit }, { classId, group_id }) {
    try {
      commit('SET_LOADING', true);
      const response = await api.post(`classes/${classId}/assign-group`, {
        group_id: group_id  // Send exactly what the API expects
      });
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  async removeGroupFromClass({ commit, dispatch }, { classId, groupId }) {
    commit('SET_LOADING', true);
    try {
      await api.delete(`/classes/${classId}/groups/${groupId}/remove`);
      
      // Refresh the class groups after successful removal
      await dispatch('fetchClassGroups', { classId });
      
      return true;
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
    async updateGroup({ commit }, { groupId, groupData }) {
      commit("SET_LOADING", true);
      try {
        const response = await api.put(`/groups/${groupId}`, groupData);
        commit("UPDATE_GROUP", response.data.data);
        return response.data;
      } catch (error) {
        commit("SET_ERROR", error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async fetchAllGroups({ commit }, { page = 1, perPage = 10, search = '' }) {
      try {
        commit('SET_LOADING', true)
        const response = await api.get('/groups', {
          params: {
            page,
            per_page: perPage,
            search
          }
        })
        commit('SET_GROUPS', response.data.data);
        commit('SET_PAGINATION', response.data.meta);
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },


    async fetchUnsignedGroups({ commit }, classId) {
      commit("SET_LOADING", true);
      try {
        const response = await api.get(`/classes/${classId}/unsigned-groups`);
        return response.data;
      } catch (error) {
        commit("SET_ERROR", error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async fetchClassGroups({ commit }, { classId, page = 1, perPage = 10, search = '' }) {
      try {
        commit("SET_LOADING", true);
        const response = await api.get(`/classes/${classId}/class-groups`, {
          params: { 
            page, 
            per_page: perPage,
            search 
          }
        });
        
        commit("SET_CLASS_GROUPS", response.data.data || []);
        commit("SET_PAGINATION", {
          current_page: response.data.meta?.current_page || 1,
          last_page: response.data.meta?.last_page || 1,
          total: response.data.meta?.total || 0,
          per_page: response.data.meta?.per_page || perPage,
          from: response.data.meta?.from || 0,
          to: response.data.meta?.to || 0
        });
        
        return response.data;
      } catch (error) {
        commit("SET_ERROR", error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async createClassGroup({ dispatch }, { classId, group_id }) {
      const response = await api.post(`/classes/${classId}/class-groups`,{ group_id: group_id});
      await dispatch("fetchClassGroups", classId);
      return response.data;
    },

    async updateClassGroup({ dispatch }, { classGroupId, groupData }) {
      const response = await api.put(`/class-groups/${classGroupId}`, groupData);
      await dispatch("fetchClassGroups", response.data.data.class_id);
      return response.data;
    },

    async deleteClassGroup({ dispatch }, classGroupId) {
      const response = await api.delete(`/class-groups/${classGroupId}`);
      await dispatch("fetchClassGroups", response.data.class_id);
      return response.data;
    },
    async createGroup({ commit }, groupData) {
      commit("SET_LOADING", true);
      try {
        const response = await api.post('/groups', groupData);
        // Ensure the response data is properly formatted
        if (response.data && typeof response.data === 'object') {
          commit("ADD_GROUP", response.data);
        }
        return response.data;
      } catch (error) {
        commit("SET_ERROR", error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },
  
    async updateGeneralGroup({ commit }, { groupId, groupData }) {
      commit("SET_LOADING", true);
      try {
        // Only send name in the request
        const response = await api.put(`/groups/${groupId}`, {
          name: groupData.name
        });
        commit("UPDATE_GROUP", response.data);
        commit("SET_GROUP", response.data);
        return response.data;
      } catch (error) {
        commit("SET_ERROR", error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },
  
    async fetchGroup({ commit }, groupId) {
      commit("SET_LOADING", true);
      try {
        const response = await api.get(`/groups/${groupId}`);
        commit("SET_GROUP", response.data);
        return response.data;
      } catch (error) {
        commit("SET_ERROR", error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async deleteGroup({ commit, state }, groupId) {
      commit('SET_LOADING', true);
      try {
        await api.delete(`/groups/${groupId}`);
        
        // Update local state
        commit('SET_GROUPS', state.groups.filter(g => g.id !== groupId));
        
        // Update pagination counts
        const newTotal = state.pagination.totalItems - 1;
        const newLastPage = Math.ceil(newTotal / state.pagination.perPage);
        
        commit('SET_PAGINATION', {
          ...state.pagination,
          totalItems: newTotal,
          totalPages: newLastPage
        });
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    }
  },
  getters: {
    getGroups: (state) => state.groups,
    getGroupById: (state) => (id) => state.groups.find((g) => g.id === id),
    getCurrentGroup: (state) => state.currentGroup,
    getCurrentGroupData: (state) => state.group,
    getPagination: state => state.pagination,
    getStudents: (state) => state.students,
    isLoading: (state) => state.isLoading,
    getError: (state) => state.error,
  },
};